import lodash from 'lodash';
import { OrganizationType } from 'src/types/organization.type';
import { getOrganizationTypeValue } from 'src/utils/organizationType.util';

// lodash is CommonJS: Vite SSR can't resolve its named exports, so destructure from the default.
const { assign, cloneDeep, isEmpty, omitBy } = lodash;

/**
 * Concept glossary used by helpers in this file:
 *
 * - **org name** (`organization.name`) — the legal/full name of the nonprofit.
 * - **nickname** (`organization.nickname`) — the short or commonly-used form.
 * - **beneficiary** (`organization.beneficiary`) — an admin-supplied custom
 *   string naming a sub-fund or specific cause within the org. When present,
 *   proceeds-related copy refers to the beneficiary instead of the org.
 * - **site name** (`siteConfig.displayName`) — the platform/tenant brand
 *   ("Nonprofit Trips", "ScoreMore Travel"). NEVER the same as org name.
 *
 * The `requiresDefiniteArticle` flag on `organization.configuration` controls
 * whether the article "The" should prefix the org name. The article is treated
 * as the first word of a proper noun ("The Marshall Thundering Herd"), so it
 * is ALWAYS capitalized — even mid-sentence — and is applied to the org's
 * name or nickname, but NOT to a beneficiary string (admins type those
 * literally).
 */

/**
 * Internal primitive — the single place where the definite article decision
 * is made. All exported helpers route their org-name output through here.
 *
 * The article, when applied, is always capitalized "The " because it is
 * treated as part of the org's proper noun.
 */
const applyArticle = (
  name: string,
  organization: OrganizationType | null | undefined,
): string => {
  if (!name) return '';
  if (!organization?.configuration?.requiresDefiniteArticle) return name;
  return `The ${name}`;
};

interface NameOptions {
  /**
   * When `false`, skip the definite article even if the org has the flag.
   * Use for tight UI labels (bar/pill labels, badges) where prose-style
   * articles read awkwardly. Default `true` (article applied per the flag).
   */
  applyArticle?: boolean;
}

/**
 * Returns the name to display in proceeds/donation messaging.
 * Resolution order: beneficiary → nickname → org name.
 *
 * Article is applied to nickname/name when the org's `requiresDefiniteArticle`
 * flag is set; never applied to beneficiary (admin-typed literal). Pass
 * `applyArticle: false` to suppress the article in label/badge contexts.
 */
export const getOrganizationProceedsName = (
  organization?: OrganizationType | null,
  options: NameOptions = {},
): string => {
  if (!organization) return '';
  if (organization.beneficiary) return organization.beneficiary;
  const base = organization.nickname || organization.name || '';
  if (options.applyArticle === false) return base;
  return applyArticle(base, organization);
};

/**
 * Returns the name to display in sharing/SEO/footer text.
 * Renders "{org} and {beneficiary}" when a beneficiary is set; otherwise
 * just the org name. A beneficiary that only repeats the org name (ignoring
 * case and surrounding spaces) is skipped, so "Badger Connect" never renders
 * as "Badger Connect and Badger Connect". Article is applied to the org-name
 * segment unless `applyArticle: false` is passed.
 */
export const getOrganizationSupportName = (
  organization?: OrganizationType | null,
  options: NameOptions = {},
): string => {
  if (!organization) return '';
  const name = organization.name || '';
  if (!name) return '';
  const articled =
    options.applyArticle === false ? name : applyArticle(name, organization);
  const beneficiary = organization.beneficiary?.trim();
  if (!beneficiary || beneficiary.toLowerCase() === name.trim().toLowerCase()) return articled;
  return `${articled} and ${beneficiary}`;
};

/**
 * Merges an organization's `configuration` over a set of defaults — normally the
 * i18n `defaultConfiguration` block — so admin-supplied copy wins wherever it is
 * set and the translated default fills in everywhere else.
 *
 * Empty config values are dropped so a blank admin field falls back to the
 * default rather than rendering nothing.
 *
 * CAUTION: lodash `isEmpty` reports `true` for booleans and numbers, so those
 * are stripped by the merge. Read flags such as `hideGeneralFund` straight from
 * `organization.configuration` — never from the object this returns.
 */
export const getOrgConfiguration = (
  organization: OrganizationType | null | undefined,
  defaults: Record<string, any> = {},
): Record<string, any> =>
  assign({}, defaults, omitBy(cloneDeep(organization?.configuration), isEmpty));

type FormatOrgNameForm = 'full' | 'short';

export interface FormatOrgNameOptions {
  /** 'full' uses the legal name; 'short' prefers nickname when set, else name. Default 'full'. */
  form?: FormatOrgNameForm;
  /** Append "'s" for possessive constructions. Default false. */
  possessive?: boolean;
  /**
   * When `false`, skip the definite article even on `form: 'full'`. Use for
   * tight UI labels (e.g. the admin schedule editor's context header).
   * Default `true` (article applied per the org's flag).
   */
  applyArticle?: boolean;
}

/**
 * Renders the organization's name for general prose use (headers, callouts,
 * UI strings that are NOT specifically about proceeds or sharing).
 *
 * Article is applied for `form: 'full'` only — `form: 'short'` is for callers
 * who deliberately want the bare nickname (e.g. tight badge labels). For
 * proceeds/sharing copy, prefer `getOrganizationProceedsName` /
 * `getOrganizationSupportName` which apply the article on top of their own
 * name-resolution logic.
 */
export const formatOrgName = (
  organization: OrganizationType | null | undefined,
  options: FormatOrgNameOptions = {},
): string => {
  if (!organization) return '';
  const { form = 'full', possessive = false, applyArticle: withArticle = true } = options;
  const base =
    form === 'short' && organization.nickname
      ? organization.nickname
      : organization.name;
  if (!base) return '';
  const articled = form === 'full' && withArticle ? applyArticle(base, organization) : base;
  return possessive ? `${articled}'s` : articled;
};

/**
 * Compare an org slug from the URL against one from an API payload.
 *
 * Slugs are lowercase by construction — npt-api's `slugify` lowercases, and
 * the admin form only accepts `^[a-z0-9]+(?:-[a-z0-9]+)*$` — but the URL is
 * whatever a partner site linked to, and partner CMS menus routinely
 * title-case it (`/c/Badger-Connect`). MySQL's case-insensitive collation
 * means the API answers either spelling happily, so a `===` gate here does
 * not read as "wrong case", it reads as "redux holds a different org" and
 * strands the page behind its skeleton (NPT-1717). Compare route slugs with
 * this; never with `===`.
 */
export const isSameOrgSlug = (
  a: string | null | undefined,
  b: string | null | undefined,
): boolean => !!a && !!b && a.toLowerCase() === b.toLowerCase();

export const GENERAL_FUND_SLUG = 'general-fund';

/**
 * Check whether a program is the site-wide shared General Fund program.
 * The site-wide General Fund row carries no owning organization (null/undefined organizationId)
 * or matches slug 'general-fund'.
 */
export const isGeneralFund = <T extends { slug?: string; organizationId?: string | null }>(
  program: T | null | undefined,
): boolean => {
  if (!program) return false;
  if (program.slug === GENERAL_FUND_SLUG) return true;
  if ('organizationId' in program && (program.organizationId === null || program.organizationId === undefined)) return true;
  return false;
};


/**
 * Single source of truth for filtering out the site-wide General Fund program.
 * Drops General Fund from the list if the list contains at least one own (non-General Fund)
 * program. Organizations that would be left with nothing keep the unfiltered list.
 */
export const filterGeneralFundPrograms = <T extends { slug?: string; organizationId?: string | null }>(
  programs: T[] | null | undefined,
): T[] => {
  if (!programs) return [];
  const own = programs.filter(p => !isGeneralFund(p));
  return own.length ? own : programs;
};

/**
 * NPT-1865 — whether an org's landing page should open with the team-select
 * modal: a youth-sports parent (has children, is nobody's child). Universities
 * and the other org types keep the inline ChapterSelector in the hero instead.
 */
export const isTeamSelectEligible = (
  organization?: OrganizationType | null,
): boolean =>
  !!organization &&
  getOrganizationTypeValue(organization) === 'YOUTH_SPORTS' &&
  (organization.childCount ?? 0) > 0 &&
  !organization.parentOrganizationId;

/**
 * Client-side fuzzy match for org-name search. 2 = substring, 1 = characters
 * appear in order ("flcnftbl" → "Falcon Football"), 0 = no match. Substring
 * hits sort above subsequence hits so the obvious matches lead the list.
 */
export const fuzzyScore = (name: string, query: string): number => {
  const n = name.toLowerCase();
  const q = query.toLowerCase();
  if (n.includes(q)) return 2;
  let matched = 0;
  for (const char of n) {
    if (char === q[matched]) matched += 1;
    if (matched === q.length) return 1;
  }
  return 0;
};
